// ---------------------------------------------------------------------------
// rateApp — the App Store rating prompt, asked at the RIGHT moment.
//
// Strategy (Apple's own guidance + Morgan's "don't be pitchy" rule): never
// interrupt, never beg. The ONLY trigger is a Nice/Gorgeous tag — the
// user just told us a road was beautiful, the happiest signal the app
// gets. (The old arrived-recap prompt is retired, Aug 6: arriving is our
// promise kept, not proof of delight, and its bare-star ratings showed it.)
//
// Gates:
//   - native app only (the web app has no App Store sheet)
//   - never mid-drive: a driving tag banks the ask; the recap close
//     after arrival pays it
//   - ONE ask per device, ever (iOS also caps at 3 per year and silently
//     drops extra requests, so worst case is a no-op)
//
// The native sheet is Apple's own (SKStoreReviewController via
// @capacitor-community/in-app-review) — the user rates without leaving
// the app, and iOS decides whether to actually show it.
// ---------------------------------------------------------------------------
import { Capacitor } from "@capacitor/core";

const TRIPS_KEY = "pleasant.completedTrips.v1";
// ONE ASK PER DEVICE, EVER (Morgan, Aug 6: "should only happen once").
// The bare 1-star ratings — stars, no words — are the signature of the
// in-app sheet catching someone at a sour moment; a repeating prompt
// multiplies those chances. One perfect moment, once. LAST_ASK_KEY is
// kept and honored so anyone already asked under the 60-day-cooldown
// era is never asked again either.
const LAST_ASK_KEY = "pleasant.ratePromptAt.v1";
/** Set when a Nice/Gorgeous tag happened mid-drive — the ask is owed, and
 *  the recap close pays it (never pop Apple's sheet at 50 mph). */
const PENDING_KEY = "pleasant.ratePending.v1";
/** carplayBridge's active-trip marker — presence means a drive is on. */
const ACTIVE_TRIP_KEY = "pleasant.activeCarTrip.v1";
/** True once this device has ever been asked — asks are single-use. */
function alreadyAsked(): boolean {
  try {
    return localStorage.getItem(LAST_ASK_KEY) != null;
  } catch {
    return true; // storage broken → err on silence
  }
}

/** Call when a trip ends with arrived=true. Bumps the counter. */
export function recordCompletedTrip(): void {
  try {
    const n = Number(localStorage.getItem(TRIPS_KEY) ?? "0") || 0;
    localStorage.setItem(TRIPS_KEY, String(n + 1));
  } catch {
    /* quota — silent */
  }
}

/** RECAP PROMPT RETIRED (Morgan, Aug 6: "get rid of the recap prompt").
 *  Arriving is OUR promise kept, not proof the user is delighted — the
 *  bare 1-star ratings likely came from this very moment. The single
 *  ask now belongs exclusively to the Nice/Gorgeous tag. This function
 *  survives only to PAY a banked mid-drive tag at the recap close (the
 *  first stationary moment after the happy signal); with nothing
 *  banked, it does nothing. */
export async function maybeRequestReview(): Promise<void> {
  try {
    if (!Capacitor.isNativePlatform()) return;
    const pending = localStorage.getItem(PENDING_KEY) === "1";
    if (!pending) return;
    localStorage.removeItem(PENDING_KEY);
    if (alreadyAsked()) return;
    localStorage.setItem(LAST_ASK_KEY, String(Date.now()));
    const { InAppReview } = await import("@capacitor-community/in-app-review");
    await InAppReview.requestReview();
  } catch {
    /* never let a rating ask break anything */
  }
}

/**
 * The happiest possible moment to ask (Morgan, Aug 6: "prompt users to
 * rate Pleasant on the App Store after they tag a place Nice or
 * Gorgeous — we are getting killed by two 1-star reviews"): the user
 * just told US the road was beautiful. Ask THEM to tell the store.
 *
 * Safety gate: if a drive is active (in-trip prompt, or the active-trip
 * marker is set), never pop Apple's sheet over a moving map — bank the
 * ask instead, and the recap close after arrival pays it. Stationary
 * tags (browsing the map, post-walk) ask right away. iOS still caps the
 * real sheet at 3 shows/year, so the worst case is always a no-op.
 */
export async function requestReviewAfterPositiveTag(opts?: { midDrive?: boolean }): Promise<void> {
  try {
    if (!Capacitor.isNativePlatform()) return;
    if (alreadyAsked()) return;
    const driving = opts?.midDrive || localStorage.getItem(ACTIVE_TRIP_KEY) != null;
    if (driving) {
      localStorage.setItem(PENDING_KEY, "1");
      return;
    }
    localStorage.setItem(LAST_ASK_KEY, String(Date.now()));
    const { InAppReview } = await import("@capacitor-community/in-app-review");
    await InAppReview.requestReview();
  } catch {
    /* never let a rating ask break anything */
  }
}
